import React, { FC, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { Player } from "./components";
import * as Styled from "./styles";

type TBottomPlayer = {
  handleCloseTrack: () => void;
  isTrackActive: boolean;
  /** Curated-playlist mode: strips the download + heart actions. */
  curated?: boolean;
};

export const BottomPlayer: FC<TBottomPlayer> = ({ handleCloseTrack, isTrackActive, curated }) => {
  const playerRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  /**
   * Hide the player once the footer comes into view, show it again on the way
   * back up.
   *
   * Re-runs on every navigation, and that is the whole point. This player mounts
   * ONCE — `PlayerLayout` sits at the top of `App`, outside the routes — while
   * the footer belongs to `SecondRootLayout`, which all 89 routes wrap
   * individually. So every page change throws the footer element away and builds
   * a new one, and an observer created on the first page went on watching a node
   * that was no longer in the document. It never fired again.
   *
   * The symptom was worse than "stops hiding": if the player happened to be
   * hidden when you navigated, nothing was left to show it, so it stayed
   * invisible for the rest of the session with a track still playing.
   */
  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;

    const setup = () => {
      const footer = document.querySelector("[data-footer]") as HTMLElement;
      if (!footer) {
        // Lazy-loaded route: the new footer is not mounted yet. Show the player
        // meanwhile — a visible player on a page with no footer is right, and a
        // player still carrying the previous page's hidden state is not.
        const player = playerRef.current;
        if (player) {
          player.style.transform = "translateY(0)";
          player.style.opacity = "1";
          player.style.pointerEvents = "auto";
        }
        retryTimeout = setTimeout(setup, 500);
        return;
      }

      observer = new IntersectionObserver(
        ([entry]) => {
          const player = playerRef.current;
          if (!player) return;
          if (entry.isIntersecting) {
            player.style.transform = "translateY(100%)";
            player.style.opacity = "0";
            player.style.pointerEvents = "none";
          } else {
            player.style.transform = "translateY(0)";
            player.style.opacity = "1";
            player.style.pointerEvents = "auto";
          }
        },
        { threshold: 0, rootMargin: "0px 0px 100px 0px" },
      );

      observer.observe(footer);
    };

    setup();

    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      observer?.disconnect();
    };
    // `isTrackActive` matters as well as the path: with no track this component
    // renders null, so `playerRef` is empty and the observer's first reading —
    // which is delivered as soon as it starts observing — lands on nothing.
    // Re-running when a track starts means a player that appears while you are
    // already down at the footer appears hidden, instead of sitting on top of it
    // until the next scroll.
  }, [pathname, isTrackActive]);

  // The bottom player is track-only; the cart lives in the header.
  if (!isTrackActive) return null;

  // `data-bottom-player` is the handle the footer-hiding check uses, the same
  // way this component finds the footer by `data-footer`. Styled-components
  // class names change on every build and cannot be asserted against.
  return (
    <Styled.BottomPlayer ref={playerRef} data-bottom-player>
      <Styled.PlayerRow>
        <Player handleCloseTrack={handleCloseTrack} curated={curated} />
      </Styled.PlayerRow>
    </Styled.BottomPlayer>
  );
};
